import { ChevronDown, Eye } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, Link, Navigate } from "react-router-dom";
import { toast } from "sonner";

import { Celebration } from "@/components/Celebration";
import { MicrositeNav } from "@/components/microsite/MicrositeNav";
import { MicrositeFooter } from "@/components/microsite/MicrositeFooter";
import { eventBase, useBrandedRedirect } from "@/lib/microsite";
import { BidBox } from "@/components/bidding/BidBox";
import { BidHistory } from "@/components/bidding/BidHistory";
import { PriceSparkline } from "@/components/bidding/PriceSparkline";
import { ProxyEducationBanner } from "@/components/bidding/ProxyEducationBanner";
import { RegisterToBidDialog } from "@/components/bidding/RegisterToBidDialog";
import { EmptyState } from "@/components/EmptyState";
import { PerformanceRelatives } from "@/components/PerformanceRelatives";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { useEventSocket, useServerCountdown } from "@/hooks/useSockets";
import { api, formatApiErrorDetail, mediaUrlFromMap } from "@/lib/api";
import { formatCurrency, formatDateTime, localized } from "@/lib/intl";
import { PUBLIC_BASE, useJsonLd, usePageMeta } from "@/lib/seo";
import { brandStyle } from "@/lib/theme";
import { SearchX } from "lucide-react";

export const bidErrorMessage = (err, t, locale, currency) => {
  const detail = err?.response?.data?.detail;
  const code = typeof detail === "object" ? detail.code : null;
  if (code === "below_minimum" && detail.min_next_bid_cents) {
    return t("bid.err_below_minimum", { amount: formatCurrency(detail.min_next_bid_cents, currency, locale) });
  }
  if (code) return t(`bid.err_${code}`, { defaultValue: formatApiErrorDetail(err.response?.data?.detail) || t("auth.genericError") });
  return formatApiErrorDetail(err.response?.data?.detail) || t("auth.genericError");
};

const CountdownCompact = ({ target, offsetRef, className = "text-2xl", testid = "lot-countdown" }) => {
  const { t } = useTranslation();
  const p = useServerCountdown(target, offsetRef);
  if (!target) return null;
  if (p.total <= 0) {
    return <span data-testid={`${testid}-ended`} className="text-sm font-medium text-[#737373]">{t("common.ended")}</span>;
  }
  const seg = (v) => String(v).padStart(2, "0");
  return (
    <span
      data-testid={testid}
      role="timer"
      className={`${className} font-semibold tabular-nums tracking-tight ${p.urgent ? "text-[#B42318] animate-pulse" : "text-[#0A0A0A]"}`}
    >
      {p.days > 0 ? `${p.days}${t("common.dShort")} ` : ""}
      {seg(p.hours)}:{seg(p.minutes)}:{seg(p.seconds)}
    </span>
  );
};

/* Dock inferior persistente SOLO móvil — capa de presentación sobre el MISMO
   estado realtime y acción placeBid de LotPage (una única suscripción WS;
   cero lógica de puja duplicada). Compacto: puja actual + countdown + CTA.
   Expandido: el BidBox existente completo (stepper −/importe/+ y Maximum Bid). */
export const BidDock = ({ lot, live, me, locale, isAuthed, busy, onBid, onOpenRegister, offsetRef, termsHref }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const myPaddle = me?.paddle_number ?? null;
  const leading = myPaddle != null && lot.leading_paddle === myPaddle && lot.bid_count > 0;
  const outbid = !leading && Boolean(me?.has_bid);
  const price = lot.bid_count > 0 ? lot.current_price_cents : lot.starting_price_cents;
  const stateKey = leading ? "bid.dockLeading" : outbid ? "bid.dockOutbid" : lot.bid_count > 0 ? "bid.currentBid" : "bid.startingPrice";

  return (
    <div className="fixed inset-x-0 bottom-0 z-40 lg:hidden" data-testid="bid-dock">
      <div
        className="bg-white/95 backdrop-blur-xl border-t border-black/10 rounded-t-3xl shadow-[0_-12px_40px_rgb(0,0,0,0.14)]"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        {expanded ? (
          <div className="px-5 pt-2 pb-4 flex flex-col gap-3 max-h-[70vh] overflow-y-auto" data-testid="bid-dock-expanded">
            <button
              type="button"
              onClick={() => setExpanded(false)}
              aria-label={t("bid.dockCollapse")}
              data-testid="bid-dock-collapse"
              className="w-full flex items-center justify-between gap-3 py-1.5"
            >
              <span className="flex flex-col text-left">
                <span className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${leading ? "text-[#1D4D3B]" : outbid ? "text-[#B42318]" : "text-[#737373]"}`}>
                  {t(stateKey)}
                </span>
                <span className="text-xl font-semibold tabular-nums tracking-tight text-[#0A0A0A]" data-testid="bid-dock-price">
                  {formatCurrency(price, lot.currency, locale)}
                </span>
              </span>
              <CountdownCompact target={lot.ends_at} offsetRef={offsetRef} className="text-lg" testid="bid-dock-countdown" />
              <ChevronDown className="w-5 h-5 text-[#737373] shrink-0" aria-hidden="true" />
            </button>
            <BidBox
              lot={lot}
              minNext={live.min_next_bid_cents}
              me={me}
              locale={locale}
              isAuthed={isAuthed}
              busy={busy}
              auctionStatus={live.auction.status}
              incrementRules={live.auction?.increment_rules}
              buyerCosts={live.buyer_costs}
              termsHref={termsHref}
              onBid={onBid}
              onOpenRegister={onOpenRegister}
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            aria-label={t("bid.dockExpand")}
            data-testid="bid-dock-compact"
            className="w-full flex items-center gap-3 px-5 py-3 text-left"
          >
            <span className="flex flex-col min-w-0">
              <span
                className={`text-[10px] font-semibold uppercase tracking-[0.18em] inline-flex items-center gap-1.5 ${leading ? "text-[#1D4D3B]" : outbid ? "text-[#B42318]" : "text-[#737373]"}`}
                data-testid="bid-dock-state"
              >
                {(leading || outbid) && (
                  <span className={`w-1.5 h-1.5 rounded-full ${leading ? "bg-[#1D4D3B]" : "bg-[#B42318] animate-pulse"}`} aria-hidden="true" />
                )}
                {t(stateKey)}
              </span>
              <span className="text-xl font-semibold tabular-nums tracking-tight text-[#0A0A0A] truncate" data-testid="bid-dock-price">
                {formatCurrency(price, lot.currency, locale)}
              </span>
            </span>
            <span className="ml-auto shrink-0">
              <CountdownCompact target={lot.ends_at} offsetRef={offsetRef} className="text-base" testid="bid-dock-countdown" />
            </span>
            <span
              className="shrink-0 inline-flex items-center justify-center h-11 px-5 rounded-full bg-[#0A0A0A] text-white text-sm font-semibold"
              data-testid="bid-dock-cta"
            >
              {t("bid.placeBid")}
              {live.min_next_bid_cents ? (
                <span className="ml-1.5 tabular-nums">{formatCurrency(live.min_next_bid_cents, lot.currency, locale)}</span>
              ) : null}
            </span>
          </button>
        )}
      </div>
    </div>
  );
};

export default function LotPage() {
  const { slug: legacySlug, eventSlug, lotId } = useParams();
  const slug = eventSlug || legacySlug;
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const { user } = useAuth();
  const [payload, setPayload] = useState(null);
  const [live, setLive] = useState(null);
  const [bids, setBids] = useState([]);
  const [busy, setBusy] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [celebrate, setCelebrate] = useState(0);
  const [flash, setFlash] = useState(false);
  // Micro-adición Fase C: relatives colapsables (abiertos en desktop, plegados en móvil)
  const [prOpen, setPrOpen] = useState(() => window.matchMedia("(min-width: 1024px)").matches);
  const wasLeadingRef = useRef(false);

  const loadLive = useCallback(async () => {
    const [{ data: liveData }, { data: history }] = await Promise.all([
      api.get(`/lots/${lotId}/live`),
      api.get(`/lots/${lotId}/bids`),
    ]);
    setLive(liveData);
    setBids(history);
  }, [lotId]);

  useEffect(() => {
    // Rebind limpio al cambiar de lote/evento: jamás retener datos del anterior
    setLive(null);
    setBids([]);
    api
      .get(`/public/events/${slug}`)
      .then(({ data }) => {
        setPayload(data);
        // B4: private event without approved access — the gate handles it, never this page
        if (!data.gated) loadLive().catch(() => setPayload(false));
      })
      .catch(() => setPayload(false));
  }, [slug, loadLive, user]);

  const eventId = live?.lot?.event_id;

  const onSocketMessage = useCallback(
    (msg) => {
      if (msg.type === "lot_update" && msg.lot?.id === lotId) {
        setLive((prev) => (prev ? { ...prev, lot: msg.lot, watchers: prev.watchers } : prev));
        if (msg.bids?.length) setBids((prev) => [...msg.bids.slice().reverse(), ...prev]);
        setFlash(true);
        setTimeout(() => setFlash(false), 700);
        api.get(`/lots/${lotId}/live`).then(({ data }) => setLive(data)).catch(() => {});
      }
      if (msg.type === "lot_extended" && msg.lot_id === lotId) {
        toast(t("bid.extendedToast"), { duration: 4000 });
        setLive((prev) => (prev ? { ...prev, lot: { ...prev.lot, ends_at: msg.ends_at, extended_count: msg.extended_count } } : prev));
      }
      if (msg.type === "lot_closed" && msg.lot?.id === lotId) {
        api.get(`/lots/${lotId}/live`).then(({ data }) => {
          setLive(data);
          if (data.me?.won) setCelebrate((c) => c + 1);
        }).catch(() => {});
      }
      if (msg.type === "auction_status" || msg.type === "auction_ended" || msg.type === "auction_live") {
        setLive((prev) => (prev ? { ...prev, auction: { ...prev.auction, status: msg.status || (msg.type === "auction_ended" ? "ended" : "live") } } : prev));
      }
    },
    [lotId, t]
  );

  const { connected, watchers, offsetRef } = useEventSocket(eventId, onSocketMessage);

  // resync state after a reconnect
  const wasConnected = useRef(false);
  useEffect(() => {
    if (connected && wasConnected.current === false && live) loadLive().catch(() => {});
    wasConnected.current = connected;
  }, [connected, loadLive]); // eslint-disable-line react-hooks/exhaustive-deps

  const lotItem = payload?.lots?.find((l) => l.lot.id === lotId);
  const horse = lotItem?.horse;

  useBrandedRedirect(payload?.organizer?.slug, payload?.event?.slug);

  usePageMeta({
    title: horse ? `${horse.name} — ${localized(payload?.event?.title, locale)}` : undefined,
    description: horse ? localized(horse.description, locale) : undefined,
    // WS9: imagen social del lote = cover del caballo (pública, sin auth).
    image: horse ? mediaUrlFromMap(payload?.media, horse.cover_media_id) : undefined,
    imageAlt: horse?.name,
  });
  useJsonLd(
    horse && lotItem
      ? {
          "@context": "https://schema.org",
          "@type": "Product",
          name: horse.name,
          description: localized(horse.description, locale) || undefined,
          category: "Sport horse",
          offers: {
            "@type": "Offer",
            price: (((live?.lot?.current_price_cents ?? lotItem.lot.current_price_cents) || lotItem.lot.starting_price_cents || 0) / 100).toFixed(2),
            priceCurrency: lotItem.lot.currency || "EUR",
            availability: "https://schema.org/InStock",
            // WS15: JSON-LD siempre sobre PUBLIC_BASE (nunca el origin del preview).
            url: `${PUBLIC_BASE}${window.location.pathname}`,
          },
        }
      : null
  );

  const placeBid = async (body, onOk) => {
    setBusy(true);
    const firstBid = !live?.me?.has_bid;
    try {
      const { data } = await api.post(`/lots/${lotId}/bids`, body);
      setLive((prev) => (prev ? { ...prev, lot: data.lot, min_next_bid_cents: data.min_next_bid_cents, me: { ...prev.me, has_bid: true, leading: data.outcome === "leading", my_max_cents: body.max_cents || prev.me?.my_max_cents } } : prev));
      if (data.outcome === "leading") {
        toast.success(body.max_cents ? t("bid.maxSet") : t("bid.bidAccepted"));
        if (firstBid || !wasLeadingRef.current) setCelebrate((c) => c + 1);
        wasLeadingRef.current = true;
      } else {
        toast(t("bid.bidDefendedToast"), { duration: 5000 });
      }
      onOk?.();
    } catch (err) {
      toast.error(bidErrorMessage(err, t, locale, live?.lot?.currency || "EUR"));
    } finally {
      setBusy(false);
    }
  };

  if (payload === false) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24">
        <EmptyState icon={SearchX} title={t("pub.notFoundTitle")} description={t("pub.notFoundDesc")} data-testid="lot-not-found" />
      </div>
    );
  }
  if (payload?.gated) {
    return <Navigate to={eventBase(payload?.organizer?.slug, slug)} replace />;
  }
  if (!payload || !live) {
    return (
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-12 grid lg:grid-cols-2 gap-10">
        <Skeleton className="h-[480px] rounded-3xl" />
        <div className="flex flex-col gap-4"><Skeleton className="h-10 w-2/3" /><Skeleton className="h-40 rounded-3xl" /><Skeleton className="h-64 rounded-3xl" /></div>
      </div>
    );
  }

  const lot = live.lot;
  const me = live.me;
  const isLiveAuction = ["live", "hybrid"].includes(live.auction?.type);
  const cover = mediaUrlFromMap(payload.media, horse?.cover_media_id);
  const closed = ["sold", "passed", "withdrawn"].includes(lot.status);
  // per-lot opening: the lot is still scheduled — shows its own future start
  const scheduledOpen = !closed && !isLiveAuction && lot.status === "upcoming" && lot.starts_at && new Date(lot.starts_at) > new Date();
  const myPaddle = me?.paddle_number ?? null;
  const leading = !closed && myPaddle != null && lot.leading_paddle === myPaddle && lot.bid_count > 0;
  const outbid = !closed && !leading && me?.has_bid;
  const chrono = bids.slice().reverse();
  const base = eventBase(payload.organizer?.slug, slug);
  // Dock móvil: solo cuando el lote está realmente abierto a pujas (timed)
  const dockVisible = !closed && !isLiveAuction && lot.status === "open" && live.auction.status !== "ended";

  return (
    <div
      style={brandStyle(payload.organizer)}
      className={dockVisible ? "pb-24 lg:pb-0" : undefined}
      data-testid="lot-page"
    >
      <Celebration trigger={celebrate} />
      <MicrositeNav
        organizer={payload.organizer}
        media={payload.media}
        backTo={{ to: base, label: localized(payload.event?.title, locale) || t("bid.backToEvent") }}
        shareTitle={horse?.name || ""}
      />
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-8 lg:py-12">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-14 items-start">
          {/* left: the horse */}
          <div className="flex flex-col gap-8">
            <figure className="relative rounded-3xl overflow-hidden bg-[#F9F9F9]">
              {cover && <img src={cover} alt={horse?.name} className="w-full h-[380px] lg:h-[480px] object-cover" loading="eager" />}
              <span className="absolute top-4 left-4 backdrop-blur-xl bg-white/85 rounded-full px-3 py-1 text-xs font-semibold text-[#0A0A0A]">
                {t("pub.lotN", { number: lot.lot_number })}
              </span>
            </figure>
            <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div className="min-w-0">
                <h1 className="text-3xl lg:text-4xl font-semibold tracking-tight text-[#0A0A0A]" data-testid="lot-horse-name">{horse?.name}</h1>
                <p className="text-sm text-[#737373] mt-1">
                  {[horse?.year_of_birth, horse?.sex && t(`horse.${horse.sex}`), horse?.studbook].filter(Boolean).join(" · ")}
                </p>
              </div>
              <Link
                to={`/horses/${horse?.id}`}
                data-testid="lot-view-horse"
                className="shrink-0 inline-flex items-center h-11 px-6 rounded-full bg-[#1D4D3B] text-white text-sm font-semibold hover:bg-[#163B2D] transition-colors duration-200"
              >
                {t("bid.viewHorse")}
              </Link>
            </div>

            {horse?.performance_relatives?.length > 0 && (
              <section className="rounded-3xl border border-black/5 p-6" data-testid="lot-performance-relatives">
                <button
                  type="button"
                  onClick={() => setPrOpen((o) => !o)}
                  aria-expanded={prOpen}
                  data-testid="lot-pr-toggle"
                  className="w-full flex items-center justify-between gap-3 text-left"
                >
                  <h2 className="text-sm font-semibold text-[#0A0A0A]">{t("pr.title")}</h2>
                  <ChevronDown
                    className={`w-4 h-4 text-[#737373] transition-transform duration-300 ${prOpen ? "rotate-180" : ""}`}
                    aria-hidden="true"
                  />
                </button>
                {prOpen && (
                  <div className="pt-5">
                    <PerformanceRelatives relatives={horse.performance_relatives} />
                  </div>
                )}
              </section>
            )}

            {chrono.length >= 2 && (
              <div className="rounded-3xl border border-black/5 p-6 flex flex-col gap-2">
                <h2 className="text-sm font-semibold text-[#0A0A0A]">{t("bid.priceEvolution")}</h2>
                <PriceSparkline bids={chrono} />
              </div>
            )}

            <div className="rounded-3xl border border-black/5 p-6">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm font-semibold text-[#0A0A0A]">{t("bid.history")}</h2>
                <span className="text-xs text-[#737373]" data-testid="lot-bid-count">{t("bid.bidsCount", { count: lot.bid_count })}</span>
              </div>
              <BidHistory bids={bids} currency={lot.currency} locale={locale} myPaddle={myPaddle} />
            </div>
          </div>

          {/* right: the auction */}
          <aside className="lg:sticky lg:top-8 flex flex-col gap-5 rounded-3xl bg-[#F9F9F8] p-6 lg:p-8" data-testid="lot-bid-panel">
            <div className="flex items-center gap-2 flex-wrap">
              {!closed && live.auction.status === "live" && (
                <Badge className="bg-[#1D4D3B] text-white hover:bg-[#1D4D3B] border-0 gap-1.5" data-testid="lot-live-badge">
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" aria-hidden="true" />
                  {t("bid.live")}
                </Badge>
              )}
              {live.auction.status === "paused" && <Badge className="bg-black/10 text-[#0A0A0A] hover:bg-black/10 border-0">{t("bid.paused")}</Badge>}
              {lot.status === "sold" && <Badge className="bg-[#0A0A0A] text-white hover:bg-[#0A0A0A] border-0" data-testid="lot-sold-badge">{t("bid.sold")}</Badge>}
              {lot.status === "passed" && <Badge className="bg-black/10 text-[#0A0A0A] hover:bg-black/10 border-0" data-testid="lot-passed-badge">{t("bid.notSold")}</Badge>}
              {lot.no_reserve && <Badge variant="outline" className="border-[#1D4D3B]/40 text-[#1D4D3B]">{t("pub.noReserve")}</Badge>}
              {lot.has_reserve && !closed && (
                <Badge
                  variant="outline"
                  data-testid="lot-reserve-badge"
                  className={lot.reserve_met ? "border-[#1D4D3B]/40 text-[#1D4D3B]" : "border-black/20 text-[#737373]"}
                >
                  {lot.reserve_met ? t("bid.reserveMet") : t("bid.reserveNotMet")}
                </Badge>
              )}
              {lot.extended_count > 0 && !closed && (
                <Badge variant="outline" className="border-[#B42318]/30 text-[#B42318]" data-testid="lot-extended-badge">
                  {t("bid.extended")} ×{lot.extended_count}
                </Badge>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#737373]">
                {closed ? t("bid.finalPrice") : lot.bid_count > 0 ? t("bid.currentBid") : t("bid.startingPrice")}
              </span>
              <span
                data-testid="lot-current-price"
                className={`text-4xl lg:text-5xl font-semibold tracking-tight tabular-nums transition-colors duration-500 ${flash ? "text-[#1D4D3B]" : "text-[#0A0A0A]"}`}
              >
                {closed
                  ? lot.final_price_cents
                    ? formatCurrency(lot.final_price_cents, lot.currency, locale)
                    : "—"
                  : formatCurrency(lot.bid_count > 0 ? lot.current_price_cents : lot.starting_price_cents, lot.currency, locale)}
              </span>
              {!closed && lot.bid_count > 0 && lot.leading_paddle != null && (
                <span className="text-xs text-[#737373]" data-testid="lot-leading-paddle">{t("bid.paddleLabel", { paddle: lot.leading_paddle })}</span>
              )}
            </div>

            {!closed && !isLiveAuction && (
              <div className="flex flex-col gap-1.5">
                {scheduledOpen ? (
                  <div className="flex flex-col">
                    <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#737373]">{t("bid.opensLabel")}</span>
                    <span data-testid="lot-opens-at" className="text-2xl font-semibold tabular-nums tracking-tight text-[#0A0A0A]">
                      {formatDateTime(lot.starts_at, locale)}
                    </span>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col">
                        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#737373]">{t("bid.endsIn")}</span>
                        <CountdownCompact target={lot.ends_at} offsetRef={offsetRef} />
                      </div>
                    </div>
                    {live.auction?.anti_sniping?.enabled && (
                      <p className="text-[11px] text-[#737373]" data-testid="lot-anti-sniping-hint">
                        {t("lot.antiSnipingHint", { min: Math.round((live.auction.anti_sniping.trigger_seconds || 300) / 60) })}
                      </p>
                    )}
                  </>
                )}
              </div>
            )}

            {closed && (
              <div className="rounded-2xl bg-white p-4 text-sm" data-testid="lot-result-panel">
                {lot.status === "sold" ? (
                  me?.won ? (
                    <p className="font-semibold text-[#1D4D3B]" data-testid="lot-you-won">{t("bid.youWon")}</p>
                  ) : (
                    <p className="text-[#0A0A0A]">{lot.winner_paddle != null ? t("bid.winnerPaddle", { paddle: lot.winner_paddle }) : t("bid.sold")}</p>
                  )
                ) : (
                  <p className="text-[#737373]">{t("bid.notSoldDesc")}</p>
                )}
              </div>
            )}

            {leading && (
              <div className="rounded-2xl bg-[#1D4D3B] text-white p-4 text-sm font-semibold" data-testid="lot-leading-banner">
                {me?.my_max_cents ? t("bid.leadingWithMax") : t("bid.leading")} · #{myPaddle}
              </div>
            )}
            {outbid && (
              <div className="rounded-2xl bg-[#B42318]/[0.08] text-[#B42318] p-4 text-sm font-semibold" data-testid="lot-outbid-banner">
                {t("bid.outbid")}
                <span className="block text-xs font-normal mt-0.5" data-testid="lot-outbid-reason">
                  {me?.last_outcome === "defended" ? t("bid.outbidProxyDesc") : t("bid.outbidDesc")}
                </span>
              </div>
            )}

            {isLiveAuction ? (
              !closed && (
                <Link
                  to={`${eventBase(payload.organizer?.slug, slug)}/live`}
                  data-testid="lot-live-room-cta"
                  className="flex items-center justify-center gap-2 h-14 rounded-2xl bg-[#0A0A0A] text-white text-base font-semibold hover:bg-black/85 transition-colors duration-300"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-[#E14434] animate-pulse" aria-hidden="true" />
                  {t("live.lotSellsLive")}
                </Link>
              )
            ) : (
              <>
                {!closed && lot.status === "open" && <ProxyEducationBanner />}
                <BidBox
                  lot={lot}
                  minNext={live.min_next_bid_cents}
                  me={me}
                  locale={locale}
                  isAuthed={Boolean(user)}
                  busy={busy}
                  auctionStatus={live.auction.status}
                  incrementRules={live.auction?.increment_rules}
                  buyerCosts={live.buyer_costs}
                  termsHref={`/auction-terms/${eventId}`}
                  onBid={placeBid}
                  onOpenRegister={() => setRegisterOpen(true)}
                />
              </>
            )}

            {Boolean(payload?.event?.buyer_premium_bps) && (
              <p className="text-xs text-[#737373] text-center" data-testid="lot-buyer-premium-note">
                {t("bid.buyerPremiumNote", { pct: payload.event.buyer_premium_bps / 100 })}
              </p>
            )}

            <div className="flex items-center justify-between text-xs text-[#737373] pt-1 border-t border-black/5">
              <span className="inline-flex items-center gap-1.5" data-testid="lot-watchers">
                <Eye className="w-3.5 h-3.5" aria-hidden="true" />
                {t("bid.watchers", { count: watchers })}
              </span>
              <span className="inline-flex items-center gap-1.5" data-testid="lot-connection">
                <span className={`w-1.5 h-1.5 rounded-full ${connected ? "bg-[#1D4D3B]" : "bg-[#B42318] animate-pulse"}`} aria-hidden="true" />
                {connected ? t("bid.connected") : t("bid.reconnecting")}
              </span>
            </div>
          </aside>
        </div>
      </div>

      <MicrositeFooter organizer={payload.organizer} eventId={eventId} />

      {dockVisible && (
        <BidDock
          key={lotId}
          lot={lot}
          live={live}
          me={me}
          locale={locale}
          isAuthed={Boolean(user)}
          busy={busy}
          onBid={placeBid}
          onOpenRegister={() => setRegisterOpen(true)}
          offsetRef={offsetRef}
          termsHref={`/auction-terms/${eventId}`}
        />
      )}

      <RegisterToBidDialog
        open={registerOpen}
        onOpenChange={setRegisterOpen}
        eventId={eventId}
        eventVisibility={live.event?.visibility || "public"}
        terms={payload.terms}
        requiredLevel={payload?.event?.verification_level || 1}
        onRegistered={(reg) => {
          setLive((prev) => (prev ? { ...prev, me: { ...(prev.me || {}), registration_status: reg.status, paddle_number: reg.paddle_number } } : prev));
          if (reg.status === "approved") setCelebrate((c) => c + 1);
        }}
      />
    </div>
  );
}
